import { useQueries } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SectionLabel,
  Spinner,
} from '@wundo/ui';
import {
  COSTS_SINCE_NOTE,
  fetchOrganisationUsageCost,
  fetchUserUsageCost,
  type UsageCostWindows,
} from '@/lib/usageCost.ts';
import { formatCurrency } from '@/utils/currencyUtils.ts';

const WINDOWS: { key: keyof UsageCostWindows; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last_7d', label: 'Last 7 days' },
  { key: 'last_30d', label: 'Last 30 days' },
  { key: 'all_time', label: 'All time' },
];

export interface UsageCostModalProps {
  open: boolean;
  onClose: () => void;
  /** Ask about a person, an organisation, or both at once. */
  userId?: string;
  userName?: string;
  organisationId?: string;
  organisationName?: string;
}

/**
 * What one user or one organisation has cost us at our vendors.
 *
 * Four windows rather than a chart: this is opened from a row in a list to
 * settle "is this the one that is expensive", and the answer to that is four
 * numbers.
 */
export function UsageCostModal({
  open,
  onClose,
  userId,
  userName,
  organisationId,
  organisationName,
}: UsageCostModalProps) {
  const results = useQueries({
    queries: [
      {
        queryKey: ['usageCost', 'user', userId],
        queryFn: () => fetchUserUsageCost(userId!),
        enabled: open && Boolean(userId),
      },
      {
        queryKey: ['usageCost', 'organisation', organisationId],
        queryFn: () => fetchOrganisationUsageCost(organisationId!),
        enabled: open && Boolean(organisationId),
      },
    ],
  });

  const subjects: { label: string; windows: UsageCostWindows }[] = [];
  if (userId && results[0].data) {
    subjects.push({ label: userName ?? 'User', windows: results[0].data });
  }
  if (organisationId && results[1].data) {
    subjects.push({
      label: organisationName ?? 'Organisation',
      windows: results[1].data,
    });
  }

  const loading = results.some((result) => result.isFetching);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>Cost</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-5 px-5 py-4'>
          {loading && subjects.length === 0 && (
            <Spinner className='size-4 text-ink-muted' />
          )}

          {subjects.map((subject) => (
            <div key={subject.label}>
              <SectionLabel as='h3'>{subject.label}</SectionLabel>
              <dl className='mt-2 grid grid-cols-2 gap-3'>
                {WINDOWS.map((window) => (
                  <div
                    key={window.key}
                    className='rounded-md bg-fill-faint px-3.5 py-2.5'
                  >
                    <dt className='text-xs text-ink-muted'>{window.label}</dt>
                    <dd className='text-base font-semibold text-ink tabular-nums'>
                      {formatCurrency(subject.windows[window.key])}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}

          <p className='text-xs text-ink-muted'>{COSTS_SINCE_NOTE}</p>
        </div>

        <DialogFooter>
          <Button variant='quiet' onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
